import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CatalogListElement } from '../shared/services/catalog.service';
import { HcSort, HcTableDataSource, PaginationComponent } from '@healthcatalyst/cashmere';
import { TypeService } from '../shared/services/type.service';
import { StatusService } from '../shared/services/status.service';
import { OktaService } from '../shared/services/okta.service';
import { SourceService } from '../shared/services/source.service';

/** Public facing list of sources  */
@Component({
    selector: 'pd-sources',
    templateUrl: './sources.component.html',
    styleUrls: ['./sources.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SourcesComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<any> = new HcTableDataSource();
    sourcesList: any[] = [];
    loading = true;
    loadError = false;
    productsDisplayedColumns: string[] = [
        'item',
        'name',
        'type',
        'status'
    ];
    sourcesPageSize: number = 10;
    filterData: string[] = [];
    filterSelections: string[] = [];
    filterType = '';
    typeFilters: string[] = [];
    statusFilters: string[] = [];
    typeText = 'View All';
    statusText = 'View All';
    typeKey: { [key: string]: string } = {};
    statusKey: { [key: string]: string } = {};

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get sourcesLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private sourceService: SourceService,
        private typeService: TypeService,
        private statusService: StatusService,
        public oktaService: OktaService
    ) {}

    ngOnInit(): void {
        this.typeService.allTypeKey.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.typeKey = list;
                if (this.statusKey) {
                    this.loadSources();
                }
            } else {
                this.typeService.loadAllTypes();
            }
        });

        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                if (this.typeKey) {
                    this.loadSources();
                }
            } else {
                this.statusService.refresh();
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        
        setTimeout(() => {
            this.initSorting();
        });
    }

    loadSources(): void {
        this.sourceService.sourceListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.sourcesList = list;

                // Parse the id fields for table content
                this.sourcesList.forEach(element => {
                    element['type'] = this.formatID( element['typeID'], 'typeID');
                    element['status'] = this.formatID( element['statusID'], 'statusID');
                });

                this.dataSource = new HcTableDataSource(this.sourcesList);
                if (this.sort && this.paginator) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                    this.initSorting();
                }
            } else {
                this.loadError = this.sourceService.loadFailed;
                if (!this.loadError) {
                    this.sourceService.refresh();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if ( id ) {
            if (type === "typeID") {
                reformattedID = this.typeKey[id.toString()];
            } else if (type === 'statusID') {
                reformattedID = this.statusKey[id.toString()];
            }
        }

        return reformattedID;
    }

    initSorting(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {     
                case 'item': {
                    if ( item.item.startsWith('AR') ) {
                        const sortStr = item.item.replace('AR', '');
                        const sortNum = Number(sortStr) + 9999999999999;
                        return sortNum;
                    } else {
                        return Number( item.item );
                    }
                }
                default: {
                    return item[property];
                }
            }
        };
    }

    applyTextFilter(text: string): void {
        this.dataSource.filter = text.trim().toLowerCase();
    }

    storeSelectedTextValue(filterArray: string[]) {
        switch (this.filterType) {
            case 'type':
                if (filterArray.length == 1) this.typeText = filterArray[0];
                else if (filterArray.length > 1) this.typeText = `${filterArray.length} selected`;
                else this.typeText = 'View All';
                break;
            case 'status':
                if (filterArray.length == 1) this.statusText = filterArray[0];
                else if (filterArray.length > 1) this.statusText = `${filterArray.length} selected`;
                else this.statusText = 'View All';
                break;
            default:
                break;
        }
    }

    storeFilterData(type: string) {
        let filterData: string[] = [];
        this.filterType = type;

        for (let i = 0; i < this.sourcesList.length; i++) {
            if (this.sourcesList[i][type]) {
                if (!filterData.includes(this.sourcesList[i][type])) {
                    filterData.push(this.sourcesList[i][type]);
                } 
            }
        }
        this.filterData = filterData;
    }

    filterProducts() {
        let filteredProducts: CatalogListElement[] = [];

        for (let i = 0; i < this.sourcesList.length; i++) {
            let typeFilterMatch = this.typeFilters.length === 0;
            if (this.sourcesList[i]['type'] && this.typeFilters.includes(this.sourcesList[i]['type'])) {
                typeFilterMatch = true;
            }

            let statusFilterMatch = this.statusFilters.length === 0;
            if (this.sourcesList[i]['status'] && this.statusFilters.includes(this.sourcesList[i]['status'])) {
                statusFilterMatch = true;
            }

            if (typeFilterMatch && statusFilterMatch) {
                filteredProducts.push(this.sourcesList[i]);
            }
        }

        //grab filter that was applied from text
        let textFilter = this.dataSource.filter;

        //set data source to the filtered product list
        this.dataSource = new HcTableDataSource(filteredProducts);

        //reset paginator and sort
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        //add the text filter
        this.dataSource.filter = textFilter;
    }

    storeFilterArray(filterArray: string[]) {
        this.storeSelectedTextValue(filterArray);

        if (this.filterType === 'type') {
            this.typeFilters = filterArray;
        } else if (this.filterType === 'status') {
            this.statusFilters = filterArray;
        }

        this.filterProducts();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
